import React,{useCallback,useEffect,useState}from"react";
import{Badge,Box,CircularProgress,IconButton,Menu,MenuItem,Typography}from"@mui/material";
import MenuIcon from"@mui/icons-material/Menu";
import NotificationsNoneOutlinedIcon from"@mui/icons-material/NotificationsNoneOutlined";
import userIcon from"../../../static/icon/user.svg";
import{useNavigate}from"react-router-dom";
import{useAuth}from"../../../context/AuthContext";

export default function Topbar({sidebarOpen,onToggleSidebar}){
  const{user,logout,authFetch}=useAuth();
  const navigate=useNavigate();

  const[userAnchor,setUserAnchor]=useState(null);
  const[notificationAnchor,setNotificationAnchor]=useState(null);
  const[companyName,setCompanyName]=useState("");
  const[notifications,setNotifications]=useState([]);
  const[unreadCount,setUnreadCount]=useState(0);
  const[notificationsLoading,setNotificationsLoading]=useState(false);

  const userMenuOpen=Boolean(userAnchor);
  const notificationMenuOpen=Boolean(notificationAnchor);

  const canViewNotifications=["ADMIN","SUPERVISOR","TECHNICIAN"].includes(user?.role);

  useEffect(()=>{
    const role=user?.role;

    if(role!=="ADMIN"&&role!=="TECHNICIAN"){
      setCompanyName("");
      return;
    }

    const loadCompany=async()=>{
      try{
        const endpoint=role==="ADMIN"
          ?"/api/login/administrator/info/"
          :"/api/login/technician/company/";

        const response=await authFetch(endpoint);
        const data=await response.json();

        if(response.ok){
          setCompanyName(data.company_name||"");
        }else{
          setCompanyName("");
        }
      }catch{
        setCompanyName("");
      }
    };

    loadCompany();
  },[user?.role,authFetch]);

  const loadNotifications=useCallback(async(showLoading=false)=>{
    if(!canViewNotifications)return;

    if(showLoading)setNotificationsLoading(true);

    try{
      const response=await authFetch("/api/login/notifications/");
      const data=await response.json();

      if(!response.ok)return;

      setNotifications(Array.isArray(data.results)?data.results:[]);
      setUnreadCount(Number(data.unread_count||0));
    }catch{
    }finally{
      if(showLoading)setNotificationsLoading(false);
    }
  },[authFetch,canViewNotifications]);

  useEffect(()=>{
    if(!canViewNotifications){
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    loadNotifications();

    const interval=setInterval(()=>{
      loadNotifications();
    },30000);

    return()=>clearInterval(interval);
  },[canViewNotifications,loadNotifications]);

  const handleNotificationOpen=(event)=>{
    setNotificationAnchor(event.currentTarget);
    loadNotifications(true);
  };

  const handleNotificationClose=()=>{
    setNotificationAnchor(null);
  };

  const openNotification=async(notification)=>{
    handleNotificationClose();

    if(!notification.is_read){
      setNotifications(prev=>prev.map(item=>
        item.public_id===notification.public_id
          ?{...item,is_read:true,read_at:new Date().toISOString()}
          :item
      ));

      setUnreadCount(prev=>Math.max(0,prev-1));

      try{
        await authFetch(`/api/login/notifications/${notification.public_id}/read/`,{
          method:"POST",
        });
      }catch{
      }
    }

    if(notification.url){
      navigate(notification.url);
    }
  };

  const handleUserClick=(event)=>{
    setUserAnchor(event.currentTarget);
  };

  const handleUserClose=()=>{
    setUserAnchor(null);
  };

  const handleLogout=async()=>{
    handleUserClose();
    await logout();
    window.location.href="/";
  };

  const roleLabel=()=>{
    if(user?.role==="SUPERADMIN")return"Superadministrador";
    if(user?.role==="ADMIN")return"Administrador";
    if(user?.role==="SUPERVISOR")return"Supervisor";
    if(user?.role==="TECHNICIAN")return"Técnico";
    return"Usuario";
  };

  const formatDate=(value)=>{
    if(!value)return"";

    try{
      return new Intl.DateTimeFormat("es-EC",{
        day:"2-digit",
        month:"2-digit",
        year:"numeric",
        hour:"2-digit",
        minute:"2-digit",
      }).format(new Date(value));
    }catch{
      return"";
    }
  };

  return(
    <Box
      sx={{
        height:62,
        bgcolor:"#ffffff",
        borderBottom:"1px solid #e4e7ec",
        px:2.5,
        display:"flex",
        alignItems:"center",
        justifyContent:"space-between",
        position:"sticky",
        top:0,
        zIndex:1100,
      }}
    >
      <Box sx={{display:"flex",alignItems:"center",gap:1.5,minWidth:0}}>
        <IconButton
          onClick={onToggleSidebar}
          size="small"
          sx={{
            borderRadius:2,
            color:"#344054",
          }}
        >
          <MenuIcon fontSize="small"/>
        </IconButton>

        {companyName&&(
          <Typography
            translate="no"
            className="notranslate"
            sx={{
              fontSize:14,
              fontWeight:700,
              color:"#182230",
              whiteSpace:"nowrap",
              overflow:"hidden",
              textOverflow:"ellipsis",
            }}
          >
            {companyName}
          </Typography>
        )}
      </Box>

      <Box sx={{display:"flex",alignItems:"center",gap:.7}}>
        {canViewNotifications&&(
          <IconButton
            size="small"
            onClick={handleNotificationOpen}
            sx={{
              width:38,
              height:38,
              borderRadius:2,
              color:"#475467",
              "&:hover":{
                bgcolor:"#f5f7fa",
              },
            }}
          >
            <Badge
              badgeContent={unreadCount}
              color="error"
              max={99}
              sx={{
                "& .MuiBadge-badge":{
                  fontSize:10,
                  minWidth:17,
                  height:17,
                  px:.4,
                },
              }}
            >
              <NotificationsNoneOutlinedIcon sx={{fontSize:21}}/>
            </Badge>
          </IconButton>
        )}

        <Box
          onClick={handleUserClick}
          sx={{
            display:"flex",
            alignItems:"center",
            gap:1.2,
            cursor:"pointer",
            borderRadius:2,
            px:1,
            py:.5,
            "&:hover":{
              bgcolor:"#f5f7fa",
            },
          }}
        >
          <Box
            component="img"
            src={userIcon}
            alt="Usuario"
            sx={{
              width:34,
              height:34,
              objectFit:"contain",
            }}
          />

          <Box
            sx={{
              display:{
                xs:"none",
                sm:"block",
              },
            }}
          >
            <Typography
              sx={{
                fontSize:13,
                fontWeight:700,
                color:"#344054",
                lineHeight:1.2,
              }}
            >
              {user?.first_name||roleLabel()}
            </Typography>

            <Typography
              sx={{
                fontSize:11.5,
                color:"#98a2b3",
                lineHeight:1.2,
              }}
            >
              {user?.email||""}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={notificationAnchor}
        open={notificationMenuOpen}
        onClose={handleNotificationClose}
        disableScrollLock
        PaperProps={{
          sx:{
            mt:1,
            width:{xs:320,sm:390},
            maxWidth:"calc(100vw - 24px)",
            maxHeight:460,
            borderRadius:2.5,
            boxShadow:"none",
            border:"1px solid #e4e7ec",
            overflow:"hidden",
          },
        }}
      >
        <Box sx={{px:2,py:1.5,borderBottom:"1px solid #eaecf0",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <Typography sx={{fontSize:14,fontWeight:700,color:"#182230"}}>
            Notificaciones
          </Typography>

          {unreadCount>0&&(
            <Typography sx={{fontSize:11.5,color:"#667085"}}>
              {unreadCount} sin leer
            </Typography>
          )}
        </Box>

        {notificationsLoading&&(
          <Box sx={{py:4,display:"flex",justifyContent:"center"}}>
            <CircularProgress size={24} sx={{color:"#123f6d"}}/>
          </Box>
        )}

        {!notificationsLoading&&notifications.length===0&&(
          <Box sx={{px:2,py:4,textAlign:"center"}}>
            <Typography sx={{fontSize:13,color:"#667085"}}>
              No tiene notificaciones.
            </Typography>
          </Box>
        )}

        {!notificationsLoading&&notifications.map(notification=>(
          <MenuItem
            key={notification.public_id}
            onClick={()=>openNotification(notification)}
            sx={{
              display:"block",
              whiteSpace:"normal",
              px:2,
              py:1.5,
              borderBottom:"1px solid #f2f4f7",
              bgcolor:notification.is_read?"#fff":"#f8fafc",
              "&:hover":{
                bgcolor:"#f5f7fa",
              },
            }}
          >
            <Box sx={{display:"flex",gap:1.2}}>
              <Box
                sx={{
                  mt:.7,
                  width:7,
                  height:7,
                  borderRadius:"50%",
                  flexShrink:0,
                  bgcolor:notification.is_read?"transparent":"#123f6d",
                }}
              />

              <Box sx={{minWidth:0,flex:1}}>
                <Typography
                  sx={{
                    fontSize:13,
                    fontWeight:notification.is_read?600:700,
                    color:"#344054",
                    lineHeight:1.35,
                  }}
                >
                  {notification.title}
                </Typography>

                {notification.message&&(
                  <Typography
                    sx={{
                      mt:.4,
                      fontSize:12.5,
                      color:"#667085",
                      lineHeight:1.45,
                    }}
                  >
                    {notification.message}
                  </Typography>
                )}

                <Typography
                  sx={{
                    mt:.7,
                    fontSize:11,
                    color:"#98a2b3",
                  }}
                >
                  {formatDate(notification.created_at)}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={userAnchor}
        open={userMenuOpen}
        onClose={handleUserClose}
        disableScrollLock
        PaperProps={{
          sx:{
            mt:1,
            minWidth:160,
            borderRadius:2,
            boxShadow:"none",
            border:"1px solid #e4e7ec",
          },
        }}
      >
        <MenuItem
          onClick={handleLogout}
          sx={{
            fontSize:13,
            fontWeight:600,
            borderRadius:1.5,
            mx:.7,
            my:.4,
          }}
        >
          Cerrar sesión
        </MenuItem>
      </Menu>
    </Box>
  );
}